import type {
  ReactNode,
} from 'react'
import {
  Button as AriaButton,
  Disclosure as AriaDisclosure,
  DisclosurePanel as AriaDisclosurePanel,
  Heading,
  type DisclosureProps as AriaDisclosureProps,
} from 'react-aria-components'

import { cn } from '@/shared/lib/cn'

interface DisclosureRenderState {
  isExpanded: boolean
}

interface DisclosureProps
  extends Omit<
    AriaDisclosureProps,
    'children' | 'className'
  > {
  trigger:
    | ReactNode
    | ((
        state: DisclosureRenderState,
      ) => ReactNode)

  children: ReactNode

  className?: string
  triggerClassName?: string
  panelClassName?: string

  triggerAriaLabel?: string

  headingLevel?:
    | 2
    | 3
    | 4
    | 5
    | 6
}

export function Disclosure({
  trigger,
  children,
  className,
  triggerClassName,
  panelClassName,
  triggerAriaLabel,
  headingLevel = 3,
  ...props
}: DisclosureProps) {
  return (
    <AriaDisclosure
      className={className}
      {...props}
    >
      {({ isExpanded }) => (
        <>
          <Heading
            level={headingLevel}
            className="m-0"
          >
            <AriaButton
              slot="trigger"
              aria-label={triggerAriaLabel}
              className={cn(
                'outline-none',
                'focus-visible:ring-2',
                'focus-visible:ring-(--color-primary)',
                'focus-visible:ring-offset-2',
                triggerClassName,
              )}
            >
              {typeof trigger === 'function'
                ? trigger({ isExpanded })
                : trigger}
            </AriaButton>
          </Heading>

          <AriaDisclosurePanel
            className={panelClassName}
          >
            {children}
          </AriaDisclosurePanel>
        </>
      )}
    </AriaDisclosure>
  )
}